// UsageDataChecker (billing, BELSŐ nézet) — a legacy `UsageDataChecker` 1:1: a kolléga a generátor ELŐTT
// megnézi a nyers külső-rendszer használati adatot (billing/service-usage, INTERNAL) + megadja a deviza-tételek
// havi manuális árfolyamát. A KÖZÖS `DataTable` + KÖZÖS `ListToolbar` (kereső + „Szűrők" popover): a HÓNAP és a
// TÍPUS is a popoverben él (user-döntés) — eddig ez a lap saját, inline szűrősávot épített, ami a
// többi lista-oldaltól eltérő felület volt. A havi manuális árfolyam megtekintés/felvitel/szerkesztés/törlés a
// `CurrencyRatePanel`-ben (a sáv akció-helyén).
// Page-gate: `usage_data.view` (az ÍRÁS — lekérés, árfolyam — `usage_data.manage`). Az adat jelenleg az ETL-migrált historikus sorokból van (a usage:fetch külső
// integráció hard-dep, nincs) — ez így is hasznos + legacy-hű (a manuális árfolyam a generátornak kell).
import { type ServiceUsageRowSchema } from '@atlas/contracts';
import {
  ArrowDownTrayIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { z } from 'zod';

import { AppShell } from '@/components/shared/app-shell';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { LabelChip } from '@/components/shared/label-chip';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListToolbar, type ToolbarFilterItem } from '@/components/shared/list-toolbar';
import { MonthStepper } from '@/components/shared/month-stepper';
import { Text } from '@/components/ui/text';
import { currentMonth, formatMonthLabel, shiftMonth } from '@/lib/month';
import { usageKeys } from '@/features/usage-data/api';
import { CurrencyRatePanel } from '@/features/usage-data/components/currency-rate-panel';
import {
  UsageFetchButton,
  useUsageFetch,
} from '@/features/usage-data/components/usage-fetch-button';
import { formatListParam, parseListParam, useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { formatDay } from '@/lib/format-datetime';
import { formatMoney } from '@/lib/format-money';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

// A SZŰRŐ search-paramok (a state-konvenció szerint linkelhető + F5-biztos nézet) — a route-nak ismernie kell
// MINDEN kulcsot, különben a router kiszűri. A default értékek nem kerülnek az URL-be.
//
// A lap az ELŐZŐ hónappal nyílik (a usage visszamenőleg érkezik — lásd a `defaults`-nál).
// A `month` és a `sourceType` lokális `useState` volt: a lap kommentje URL-állapotot ígért, de
// egy megosztott link vagy egy F5 az AKTUÁLIS hónapra és „mind" típusra esett vissza — a többi 21 lista-oldal
// mind URL-alapú (`useListFilters`).
const usageDataSearchSchema = z.object({
  q: z.string().optional(),
  month: z.string().optional(),
  sourceType: z.string().optional(),
  assignment: z.string().optional(),
});

export const Route = createFileRoute('/usage-data')({
  validateSearch: (search) => usageDataSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: UsageDataPage,
});

type UsageRow = z.infer<typeof ServiceUsageRowSchema>;

const PAGE_SIZE = 100;
const ALL = '__all__';

// A 8 usage-source-type (a contract UsageSourceTypeSchema 1:1) — a címkék az i18n-ből.
const SOURCE_TYPES = [
  'newsletter',
  'loyalty_newsletter',
  'rs_loyalty',
  'rs_direct_referral',
  'rs_direct',
  'rs_referral',
  'rs_voucher',
  'ticket',
] as const;
type SourceType = (typeof SOURCE_TYPES)[number];

export function UsageDataPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('usage_data.view');
  const locale = i18n.language;

  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet. A hónap defaultja
  // az AKTUÁLIS hónap — az `useMemo` azért kell, hogy a default-objektum ne változzon renderenként.
  /**
   * A LAP AZ ELŐZŐ HÓNAPPAL NYÍLIK, nem a folyóval (user-döntés).
   *
   * MIÉRT: a használati adatot **visszamenőleg** húzzuk le — egy adott hónapban a MÚLT havi forgalom van meg
   * (a cron is így jár el: a lezárt hónapot kérdezi). A folyó hónapra nyitva a lap tehát a legtöbbször ÜRES
   * listát adott, és a felhasználó első dolga volt visszaléptetni egy hónapot.
   *
   * A hónap az URL-ben él, tehát a linkelt nézet változatlanul azt a hónapot hozza, amire mutat.
   */
  const defaults = useMemo(
    // a típus-szűrő TÖBB-választós (#52): vesszős lista az URL-ben, üres = mind
    () => ({ q: '', month: shiftMonth(currentMonth(), -1), sourceType: '', assignment: ALL }),
    [],
  );
  const { filters: urlFilters, setFilters } = useListFilters(defaults);
  const q = urlFilters.q;
  const month = urlFilters.month;
  const sourceTypes = useMemo(
    () => parseListParam(urlFilters.sourceType) as SourceType[],
    [urlFilters.sourceType],
  );
  const setMonth = useCallback((v: string) => setFilters({ month: v }), [setFilters]);
  const setSourceTypes = useCallback(
    (v: string[]) => setFilters({ sourceType: formatListParam(v) }),
    [setFilters],
  );
  const assignment = urlFilters.assignment;
  const setAssignment = useCallback((v: string) => setFilters({ assignment: v }), [setFilters]);

  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  const [yearStr, monthStr] = month.split('-');
  const year = Number(yearStr);
  const monthNum = Number(monthStr);

  // Page-gate: `usage_data.view` nélkül meg sem kérdezzük a szervert (a BFF is 403-at adna).
  const gated = !permsLoading && !canView;
  const canManageUsage = can('usage_data.manage');
  // A havi lekérés vezérlője EGY példányban (a sáv-gomb és a mobil-FAB ugyanazt az állapotot látja), az
  // árfolyam-panel nyitása pedig innen is vezérelhető — mobilon a FAB nyitja.
  const usageFetch = useUsageFetch({ month, monthLabel: formatMonthLabel(month, locale), t });
  const [ratesOpen, setRatesOpen] = useState(false);

  // a query-kulcsba a vesszős URL-alak megy (stabil sztring, nem tömb-referencia)
  const filters = { year, month: monthNum, sourceType: urlFilters.sourceType, assignment, q };
  const listKey = usageKeys.usageList(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.serviceUsageList.query({
        query: {
          year,
          month: monthNum,
          sourceType: sourceTypes.length > 0 ? sourceTypes : undefined,
          assignment: assignment === 'missing' ? 'missing' : undefined,
          q: q || undefined,
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(() => pages.flatMap((p) => (p.status === 200 ? p.body.rows : [])), [pages]);
  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters = q !== '' || sourceTypes.length > 0 || assignment !== ALL;

  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  const emDash = <span className="text-muted-foreground">—</span>;

  // A HÓNAP NEM A POPOVERBEN VAN, hanem MINDIG LÁTHATÓAN a sáv elején — ugyanaz a döntés, mint a Pénzügy
  // lapon: ez a lap FŐ DIMENZIÓJA (a felhasználó léptetgeti), popover mögé rejtve a leggyakoribb művelet
  // lenne a legdrágább. Aktív szűrőnek továbbra sem számít: mindig van értéke.
  //
  // MIÉRT NEM ZAJOSABB EMIATT A SÁV: a léptető a KIVETT szűrő helyére lép, nem mellé — a popover eggyel
  // kevesebb tételt hord, a sáv pedig ugyanannyi elemet mutat, csak a leggyakrabban használtat elöl.
  const filterItems: ToolbarFilterItem[] = [
    {
      kind: 'multi',
      key: 'sourceType',
      label: t('usageData.filterType'),
      values: sourceTypes,
      onChange: setSourceTypes,
      options: SOURCE_TYPES.map((s2) => ({ value: s2, label: t(`usageData.sourceType.${s2}`) })),
    },
    {
      // HIÁNYZÓ HOZZÁRENDELÉS: a lekérés minden visszaadott sort eltárol (a fiókhoz nem kötötteket is) — ez a
      // szűrő csak azokat a mért sorokat mutatja, amelyekből ma nem lesz számla, mert nincs mihez kötni.
      kind: 'select',
      key: 'assignment',
      label: t('usageData.filterAssignment'),
      value: assignment,
      onChange: setAssignment,
      allValue: ALL,
      options: [{ value: 'missing', label: t('usageData.assignmentFilter.missing') }],
    },
  ];

  const columns: DataTableColumn<UsageRow>[] = [
    {
      // 1. Típus — a usage-source-type lokalizált neve (a legacy fő-oszlop).
      id: 'usageSourceType',
      header: t('usageData.columns.sourceType'),
      cardLabel: t('usageData.columns.sourceType'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      cell: (r) => (
        <div className="min-w-0 space-y-0.5">
          <Text variant="itemTitle" as="span" className="block truncate">
            {t(`usageData.sourceType.${r.usageSourceType}`, { defaultValue: r.usageSourceType })}
          </Text>
        </div>
      ),
    },
    {
      // 2. Forrás-azonosító + feloldott fiók(ok) — a külső rendszer ID + a hozzá tartozó fiók-nevek.
      //
      // TIPOGRÁFIA (user-döntés): a sor-fej (a külső ID) a 3. oszlop betűje — `text-muted-foreground`
      // az oszlopon, sima szöveg a sejtben (NEM `Text variant="body"`, ami erősebb kontrasztot adott). A
      // fiók-név a SZÁMLÁZÁSI PROFILOK cím-oszlopának „ország" sora: `tone="tertiary"`, tehát halványabb és
      // vékonyabb — így a saját ID-ja alatt másodlagos információként olvasható.
      //
      // A `font-mono` MEGMARAD az ID-n: külső rendszer azonosítója, a fix szélesség segíti az összevetést.
      // Előtte „ID" CÍMKE-CHIP (a közös `LabelChip` — ugyanaz, mint a számlázási profilok `HU`/`EU` chipje):
      // a nyers szám így nem olvasódik mennyiségnek/dátumnak (user-kérés).
      id: 'sourceReferenceId',
      header: t('usageData.columns.source'),
      cardLabel: t('usageData.columns.source'),
      card: 'subtitle',
      width: 'minmax(0,2fr)',
      className: 'text-muted-foreground',
      multiline: true,
      cell: (r) => (
        <div className="min-w-0 space-y-0.5">
          <div className="flex items-center gap-1">
            {/* A chip MEGMONDJA, MELYIK RENDSZER azonosítója (user-kérés): a puszta „ID" nem árulta el, hogy
                MailWizz- vagy HotelCMS-azonosítót lát-e a kezelő, pedig a kettő más rendszerben keresendő.
                Ismeretlen forrásnál marad a semleges „ID" — tippelni rosszabb, mint hallgatni. */}
            {/* A `?? null` NEM felesleges: egy RÉGEBBI (még nem újraindult) BFF válaszából a mező hiányzik,
                és a ts-rest a választ nem validálja — `undefined` mellett a nyers i18n-kulcs jelent meg a
                chipben (mérve a dev-szerveren). A semleges „ID" a helyes visszaesés. */}
            <LabelChip
              label={
                (r.sourceSystem ?? null) === null
                  ? t('usageData.idChip')
                  : t(`usageData.sourceSystemChip.${r.sourceSystem}`)
              }
            />
            <span className="truncate font-mono">{r.sourceReferenceId}</span>
          </div>
          {r.accountNames.length === 0 ? (
            <Text tone="tertiary" as="div" className="truncate">
              {t('usageData.noAccount')}
            </Text>
          ) : (
            <ExpandableCell
              items={r.accountNames}
              getKey={(n, i) => `${n}-${i}`}
              visible={2}
              threshold={3}
              moreLabel={(n) => t('usageData.more', { count: n })}
              triggerAriaLabel={t('usageData.moreItems')}
              renderItem={(n) => (
                <Text tone="tertiary" as="span" className="block truncate">
                  {n}
                </Text>
              )}
            />
          )}
        </div>
      ),
    },
    {
      // 3. Időszak — év/hó (a havi keresés dimenziója).
      id: 'period',
      header: t('usageData.columns.period'),
      cardLabel: t('usageData.columns.period'),
      width: 'minmax(0,1fr)',
      className: 'text-muted-foreground',
      card: 'hidden',
      cell: (r) => (
        <span>{formatMonthLabel(`${r.year}-${String(r.month).padStart(2, '0')}`, locale)}</span>
      ),
    },
    {
      // 4. KAPOTT ADAT — a nyers `usage_quantity`, MÉRTÉKEGYSÉGGEL.
      //
      //    MIÉRT NEM „Mennyiség" (user-döntés): ugyanez az oszlop forrás-típusonként MÁST mér — a hírlevélnél
      //    kiküldött DARABOT, a Loyalty regisztrációnál FŐT, a jutalékos soroknál pedig MÁR AZ ÖSSZEGET. A
      //    puszta szám így nem volt olvasható: a 123 456 lehetett hírlevél-darab és forint is.
      //
      //    A MÁSODIK SOR a nem-jutalékos soroknál a mennyiségből SZÁMOLT nettó összeg — ugyanazzal a motorral,
      //    amivel a havi számla számol. Tipográfiája a 2. oszlop második soráé (`tone="tertiary"`: halványabb
      //    és vékonyabb), mert másodlagos információ a kapott adat alatt.
      //
      // BALRA igazított, mint minden oszlop (rendszer-alapértelmezés, user-döntés): a korábbi
      //    jobbra-zárás a fejlécet és a cellát is a többi oszloptól eltérő élre tette.
      id: 'usageQuantity',
      header: t('usageData.columns.measured'),
      cardLabel: t('usageData.columns.measured'),
      card: 'trailing',
      width: 'minmax(0,1.2fr)',
      // A 3. oszlop betűje (user-döntés): halvány, nem kiemelt — a kapott adat nyers, nem a
      // sor azonosítója. A `tabular-nums` marad: a számjegyek fix szélességűek, a számok egy vonalban futnak.
      className: 'text-muted-foreground',
      multiline: true,
      cell: (r) =>
        r.usageQuantity === null ? (
          emDash
        ) : (
          <div className="min-w-0 space-y-0.5">
            <span className="tabular-nums">
              {Number(r.usageQuantity).toLocaleString(locale)} {unitLabel(r, t)}
            </span>
            {/* ÁG-BONTÁS a KOMBINÁLT RoomSome-soron. A lap ilyenkor EZT az egy sort mutatja a két ág sora
                HELYETT (a szerződés a modul-végösszegre szól), tehát a bontásnak itt kell látszania —
                különben a szám forrása láthatatlan. A két ág összege mindig a kiírt összeg. */}
            {r.commissionBranches && (
              <div className="text-subtle space-y-0.5 text-xs">
                {r.commissionBranches.direct && (
                  <div className="tabular-nums">
                    {t('usageData.branch.direct', {
                      amount: Number(r.commissionBranches.direct.amount).toLocaleString(locale),
                      currency: r.quantityCurrency ?? '',
                      count: r.commissionBranches.direct.bookings,
                    })}
                  </div>
                )}
                {r.commissionBranches.inquiry && (
                  <div className="tabular-nums">
                    {t('usageData.branch.inquiry', {
                      amount: Number(r.commissionBranches.inquiry.amount).toLocaleString(locale),
                      currency: r.quantityCurrency ?? '',
                      count: r.commissionBranches.inquiry.bookings,
                    })}
                  </div>
                )}
              </div>
            )}
            <UsageAmount row={r} sourceType={r.usageSourceType} locale={locale} />
            {/* `?? []` / `=== true`: a telepítési ablakban (régebbi BFF) a két mező hiányozhat — akkor nincs bontás */}
            {(r.consumers ?? []).length > 0 && <UsageConsumers row={r} locale={locale} />}
            {r.splitMismatch === true && (
              <div className="text-warning flex items-center gap-1 text-xs font-light">
                <ExclamationTriangleIcon className="size-3.5 shrink-0" aria-hidden />
                <span>{t('usageData.splitMismatch')}</span>
              </div>
            )}
          </div>
        ),
    },
    {
      // 5. MEGJEGYZÉS — miért LESZ vagy NEM LESZ ebből a mérésből számla-tétel. A generálási kihagyás mintája:
      //    badge (a jelentés egy pillantásra) + alatta a magyarázó mondat (MIT jelent, és mi a teendő). A korábbi
      //    „nem ebből számlázunk" felirat a típus alatt nem mondta meg, MIÉRT nem (user-lelet).
      id: 'billingNote',
      header: t('usageData.columns.note'),
      cardLabel: t('usageData.columns.note'),
      width: 'minmax(0,1.8fr)',
      multiline: true,
      cell: (r) =>
        // `== null`: a mező a telepítési ablakban (régebbi BFF) hiányozhat — akkor nincs mit kimondani.
        r.billingNote == null ? (
          emDash
        ) : (
          <div className="min-w-0 space-y-1">
            <DomainBadge
              kind="usageBillingNote"
              value={r.billingNote.code}
              label={t(`usageData.billingNote.label.${r.billingNote.code}`)}
            />
            <Text tone="tertiary" as="div" className="text-xs">
              {t(
                // LEZÁRT, azóta inaktív sor: „számlázunk", de a mondat kimondja, hogy a záró dátumig
                `usageData.billingNote.desc.${
                  r.billingNote.code === 'billed' && r.billingNote.inactive != null
                    ? 'billed_closed'
                    : r.billingNote.code
                }`,
                {
                  sourceType: t(`usageData.sourceType.${r.usageSourceType}`, {
                    defaultValue: r.usageSourceType,
                  }),
                  usedSourceType:
                    r.billingNote.usedSourceType === null
                      ? ''
                      : t(`usageData.sourceType.${r.billingNote.usedSourceType}`, {
                          defaultValue: r.billingNote.usedSourceType,
                        }),
                  ref: r.sourceReferenceId,
                  // `ended` / `not_started`: a dátum · `billed_inactive`: MI inaktív (a régebbi BFF-ből hiányozhat)
                  date: formatDay(r.billingNote.date ?? null, locale),
                  inactive:
                    r.billingNote.inactive == null
                      ? ''
                      : r.billingNote.code === 'billed_inactive'
                        ? t(`usageData.billingNote.inactiveState.${r.billingNote.inactive}`)
                        : t(`usageData.billingNote.inactive.${r.billingNote.inactive}`),
                },
              )}
            </Text>
          </div>
        ),
    },
  ];

  return (
    <AppShell
      secondaryActions={
        // MOBIL: a sáv két írás-vezérlője (havi lekérés · árfolyam) rejtve van — a FAB-ban elérhető marad.
        // Jog nélkül egyik sem kerül be: a szerver 403-mal zárná őket.
        canManageUsage
          ? [
              {
                key: 'usage-fetch',
                icon: <ArrowDownTrayIcon />,
                label: usageFetch.pending
                  ? t('usageData.fetch.running')
                  : t('usageData.fetch.action'),
                onClick: usageFetch.run,
              },
              {
                key: 'rates',
                icon: <BanknotesIcon />,
                label: t('usageData.rates.title'),
                onClick: () => setRatesOpen(true),
              },
            ]
          : []
      }
      toolbar={
        <ListToolbar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            // A placeholder a KÖZÖS `common.searchPlaceholder` (CC2): amit a kereső NÉZ, az a mezők dolga.
            placeholder: t('common.searchPlaceholder'),
          }}
          // A HÓNAP és a TÍPUS a szűrő-popoverben (sorrend-tartó lista): a hónap DIMENZIÓ (mindig van értéke,
          // ezért nem számol az aktív-szűrő badge-be), a típus valódi szűkítés.
          leading={<MonthStepper month={month} onMonthChange={setMonth} />}
          filterItems={filterItems}
          meta={
            infinite.isFetching && !infinite.isFetchingNextPage
              ? t('usageData.loading')
              : !isError && !infinite.isLoading
                ? t('usageData.countOf', { shown: rows.length, total })
                : ''
          }
          // JOBB OLDAL: a látott hónap LEKÉRÉSE a külső rendszerekből + a havi manuális árfolyam. Mindkettő
          // ÍRÁS, tehát `usage_data.manage` — a lap MEGTEKINTÉSE (`usage_data.view`) önmagában nem elég
          // hozzájuk. Jog nélkül meg sem jelennek: a szerver 403-mal zárná őket.
          //
          // MOBILON EGYIK SEM LÁTSZIK (`hidden md:flex`): a szűk sávban a kereső + „Szűrők" az elsődleges, a
          // két írás-vezérlő pedig a lebegő FAB-ba került — a repo lista-mintája. Enélkül ugyanaz a gomb
          // KÉTSZER lenne kint mobilon (a sávban és a FAB-ban is).
          actions={
            <div className="hidden items-center gap-2 md:flex">
              <UsageFetchButton
                month={month}
                monthLabel={formatMonthLabel(month, locale)}
                canManage={canManageUsage}
                t={t}
              />
              <CurrencyRatePanel
                month={month}
                canManage={canManageUsage}
                open={ratesOpen}
                onOpenChange={setRatesOpen}
                t={t}
              />
            </div>
          }
        />
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<UsageRow>
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('usageData.noMatches')}
                description={t('usageData.noMatchesDesc')}
              />
            ) : (
              <EmptyState title={t('usageData.empty')} description={t('usageData.emptyDesc')} />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
        />
      </div>
    </AppShell>
  );
}

// ── A KAPOTT ADAT mértékegysége és pénzbeli oldala ──────────────────────────────────────────────────────

/**
 * A kapott adat MÉRTÉKEGYSÉGE forrás-típus szerint: a hírlevél-forrásoknál kiküldött DARAB, a Loyalty
 * regisztrációnál FŐ, a jutalékos soroknál pedig PÉNZ — ott a devizát a szerver adja (`quantityCurrency`,
 * a forrás-rendszer válaszából), mert az soronként eltérhet.
 */
function unitLabel(row: UsageRow, t: TFunction): string {
  if (row.quantityCurrency !== null) return row.quantityCurrency;
  return row.usageSourceType === 'rs_loyalty'
    ? t('usageData.unit.person')
    : t('usageData.unit.count');
}

/** Egy összeg és a jelölései — a sor maga, vagy a bontás egy hozzárendelése (a szerződés közös mezői). */
type AmountLike = Pick<
  UsageRow,
  | 'amount'
  | 'amountBase'
  | 'amountCurrency'
  | 'amountNote'
  | 'amountShared'
  | 'amountFrequency'
  | 'amountPeriodStart'
  | 'amountPeriodEnd'
  | 'amountProrated'
>;

/**
 * A kapott mennyiségből SZÁMOLT nettó összeg (második sor) — vagy a hiány KIMONDOTT oka.
 *
 * A `no_rate` az egyetlen TEENDŐ-jelzés (fel kell vinni a hónap kurzusát), ezért az kap figyelmeztető jelet és
 * danger-színt; a TÖBBI ok csak magyarázat, azok a halvány, másodlagos betűvel állnak. Ami nem magyarázható
 * (nincs hozzárendelés, jutalékos sor), ott NINCS második sor — az üres hely a legkevésbé zajos válasz.
 *
 * ALAPDÍJ + SÁVOS tételnél MINDKÉT rész látszik (a bizonylat két tétele): „alapdíj 70 000 + regisztráltak után
 * 10 000". Csak a mért részt mutatva nem derült ki a teljes díj; csak a teljeset mutatva úgy tűnt, a mérés kerül
 * ennyibe. MEGOSZTOTT HASZNÁLATNÁL alatta az is, miből jön a rész („55 000 Ft, 3 hotel között").
 */
function UsageAmount({
  row,
  sourceType,
  locale,
}: {
  row: AmountLike;
  sourceType: string;
  locale: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (row.amount !== null && row.amountCurrency !== null) {
    // AZ ÖSSZEG MELLETTI JELÖLÉSEK. Az összeg PONTOSAN az a tétel-nettó, amit a hónap generálása kiállít —
    // két dolgot viszont ki kell mondani, különben a szám mást ígér, mint amit takar:
    //   • NEM-HAVI gyakoriságnál egy TELJES PERIÓDUS díja (különben havi díjnak látszik),
    //   • az ELSŐ számla ARÁNYOSÍTOTT (a periódus első hónapjának fele hiányzik).
    // A `?? null` védelem ugyanaz, mint a forrás-chipen: hiányzó mezőnél nincs jelölés, nem nyers kulcs.
    const freq = row.amountFrequency ?? null;
    const marks = [
      freq === 'quarterly' || freq === 'semi_annual' || freq === 'annual'
        ? t(`usageData.amountPeriod.${freq}`)
        : null,
      row.amountProrated === true ? t('usageData.amountProrated') : null,
    ].filter((m): m is string => m !== null);
    // A SZÁMLÁZOTT IDŐSZAK tooltipben: a sávban zaj lenne, de a nem-havi tételnél ez mondja meg, melyik
    // periódust nyitja a hónap.
    const range =
      row.amountPeriodStart !== null && row.amountPeriodEnd !== null
        ? t('usageData.amountPeriodRange', {
            start: formatDay(row.amountPeriodStart, locale),
            end: formatDay(row.amountPeriodEnd, locale),
          })
        : undefined;
    const money = (v: string): string => formatMoney(v, row.amountCurrency ?? 'HUF', locale);
    const base = row.amountBase ?? null;
    const shared = row.amountShared ?? null;
    return (
      <div className="min-w-0">
        <Text
          tone="tertiary"
          as="div"
          className="tabular-nums"
          {...(range ? { title: range } : {})}
        >
          (
          {base === null
            ? money(row.amount)
            : t(
                sourceType === 'rs_loyalty'
                  ? 'usageData.amountParts.registrants'
                  : 'usageData.amountParts.measured',
                { base: money(base), measured: money(row.amount) },
              )}
          {marks.length > 0 && <span className="ml-1">· {marks.join(' · ')}</span>})
        </Text>
        {shared !== null && (
          <Text tone="tertiary" as="div" className="text-xs tabular-nums">
            {t('usageData.amountShared', { total: money(shared.total), count: shared.count })}
          </Text>
        )}
      </div>
    );
  }
  if (row.amountNote === null) return null;
  if (row.amountNote === 'no_rate') {
    return (
      <div className="text-danger flex items-center gap-1 text-xs font-light">
        <ExclamationTriangleIcon className="size-3.5 shrink-0" aria-hidden />
        <span>{t('usageData.amountNote.no_rate')}</span>
      </div>
    );
  }
  return (
    <Text tone="tertiary" as="div">
      {t(`usageData.amountNote.${row.amountNote}`)}
    </Text>
  );
}

/**
 * HOZZÁRENDELÉSENKÉNTI BONTÁS: ha több hozzárendelés is fogyasztja a mérést (pl. több hotel ugyanazon a
 * loyalty-programon), mindegyik a saját összegével, a saját számlájára. Korábban itt csak annyi állt, hogy „több
 * hozzárendelés" — épp a csoportos tételeket nem lehetett ellenőrizni (user-lelet).
 */
function UsageConsumers({ row, locale }: { row: UsageRow; locale: string }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-0.5 pt-0.5">
      <Text tone="tertiary" as="div" className="text-xs">
        {t('usageData.consumers.title', { count: row.consumers.length })}
      </Text>
      <ExpandableCell
        items={row.consumers}
        getKey={(c) => c.assignmentId}
        visible={3}
        threshold={4}
        moreLabel={(n) => t('usageData.more', { count: n })}
        triggerAriaLabel={t('usageData.moreItems')}
        contentClassName="min-w-72"
        renderItem={(c) => (
          <div className="min-w-0 border-l pl-2">
            <Text tone="tertiary" as="div" className="truncate text-xs">
              {c.ownerName ?? t('usageData.consumers.unknownOwner')}
              {c.planName ? ` · ${c.planName}` : ''}
            </Text>
            <UsageAmount row={c} sourceType={row.usageSourceType} locale={locale} />
          </div>
        )}
      />
    </div>
  );
}
